import React, { useEffect, useState } from 'react'
import { useOverlay } from '@fs/zion-ui'
import { getPreference, setPreference } from '@fs/zion-preferences'
import {
  useNamesExperienceToggle,
  NOT_TURNED_ON_SEEN_KEY,
  ALREADY_TURNED_ON_SEEN_KEY,
} from '../../../../hooks/useNamesExperienceOptIn'
import AlreadyTurnedOnDialog from './AlreadyTurnedOnDialog'
import NotTurnedOnDialog from './NotTurnedOnDialog'

/**
 * Persist a dialog's seen-flag, fire-and-forget. Keeps its own catch: the write is deliberately not
 * chained into the caller's promise, so the caller's catch would not see a rejection here.
 *
 * @param {string} key - The seen-key preference to set.
 * @returns {void}
 */
function markSeen(key) {
  setPreference(key, true).catch((error) => {
    console.error(`[AttachFlowOnboardingDialog] Failed to mark ${key} as seen:`, error)
  })
}

/**
 * Shows the one-time onboarding dialog for the updated Names experience: the opt-in invite for
 * opted-out users, or the changelog for those already opted in.
 *
 * The two are mutually exclusive, so the gate is "neither dialog seen yet" rather than per-dialog -
 * a user who saw the changelog and later turned the experience off must not be invited back in.
 *
 * @returns {JSX.Element}
 */
export default function AttachFlowOnboardingDialog() {
  const { isOptedIn, turnOn, loading, rolloutEnabled } = useNamesExperienceToggle()
  const alreadyTurnedOnOverlay = useOverlay()
  const notTurnedOnOverlay = useOverlay()
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    // Wait for the opt-in state to resolve; otherwise isOptedIn reads its false default and always
    // picks the invite. The loading dep re-runs this once it settles.
    if (checked || loading) return undefined
    // Master kill-switch (also covers signed-out visitors): skip onboarding entirely.
    if (!rolloutEnabled) return undefined
    let mounted = true

    // Both keys in one request - the multi-key form resolves to { [key]: { value } }, not a value.
    getPreference(NOT_TURNED_ON_SEEN_KEY, ALREADY_TURNED_ON_SEEN_KEY)
      .then((seenByKey) => {
        if (!mounted) return
        // getPreference resolves to exactly null when there is no user session, which can disagree
        // with the signedIn check above (helpee mode, or a session lost in between). Bail rather than
        // open a dialog whose seen-write is guaranteed to fail, which would reopen it every visit.
        if (seenByKey === null) return

        const hasSeenEither =
          !!seenByKey?.[NOT_TURNED_ON_SEEN_KEY]?.value || !!seenByKey?.[ALREADY_TURNED_ON_SEEN_KEY]?.value
        if (hasSeenEither) return

        // Mark seen BEFORE opening: the component remounts whenever the user leaves and returns to
        // the Names view, resetting `checked`, so only the persisted write survives a remount.
        // It is not a hard guarantee - the write is fire-and-forget, so a remount within that one
        // PUT round-trip can still re-read unset and reopen the dialog. Writing first shrinks the
        // window to about as small as it goes without blocking the dialog on a network call.
        if (isOptedIn) {
          markSeen(ALREADY_TURNED_ON_SEEN_KEY)
          alreadyTurnedOnOverlay.open()
        } else {
          markSeen(NOT_TURNED_ON_SEEN_KEY)
          notTurnedOnOverlay.open()
        }
      })
      .catch((error) => {
        console.error('[AttachFlowOnboardingDialog] Failed to check onboarding preference:', error)
      })
      .finally(() => {
        if (mounted) setChecked(true)
      })

    return () => {
      mounted = false
    }
    // isOptedIn/overlays intentionally excluded to avoid re-checking on toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, loading, rolloutEnabled])

  // Accepting the invite does not chain into the changelog; turnOn marks it seen instead.
  return (
    <>
      <AlreadyTurnedOnDialog overlay={alreadyTurnedOnOverlay} />
      <NotTurnedOnDialog overlay={notTurnedOnOverlay} onTurnOn={turnOn} />
    </>
  )
}
